import type { AbilityLevel, LoopState, PermanentState } from "../types"
import {
  abilities,
  getFactionById,
  getLocationById,
  getRouteById,
  getRoutesForLocation,
  getUnitById,
} from "../game/content"
import {
  describeRequirement,
  describeTrustRequirement,
  getAbilityLevel,
  getUpcomingLevel,
  isAtLeastLevel,
  type UpcomingAbilityLevel,
} from "../game/abilityEngine"
import { isTrustAtLeast } from "../game/factionEngine"
import { getFactionActionsAt } from "../game/questEngine"
import { getAbilitySpriteStyle, getFactionCrestStyle, getLocationSpriteStyle } from "../game/sprites"
import {
  canDiscoverIdentity,
  canSeverAntiPlayer,
  describeSeveranceRequirements,
  isAwaitingReckoning,
} from "../game/antiPlayerEngine"
import { effectiveTravelDays, getTravelBlockReason, isRouteOpen } from "../game/travelEngine"
import { formatDay } from "../game/calendarEngine"
import { getTravelPathTo } from "../game/scheduleEngine"
import { getStacksAtLocation } from "../game/armyEngine"
import { getCurrentArmorTier, getNextArmorTier } from "../game/armorEngine"

interface LocationScreenProps {
  permanent: PermanentState
  loop: LoopState
  locationId: string
  /** The far end of a journey in progress, if the player is part way through one. */
  travelDestinationId: string | null
  onTravel: (routeId: string, finalDestinationId?: string) => void
  onCancelJourney: () => void
  onTrainAbility: (abilityId: string, level: Exclude<AbilityLevel, "none">) => void
  onOpenDialogue: (dialogueId: string) => void
  onRest: () => void
  onWarnGuard: () => void
  onEvacuate: () => void
  onInvestigateRuins: () => void
  onRallyGoldenfieldsMilitia: () => void
  onEvacuateGoldenfields: () => void
  onSendWordToCrownreach: () => void
  onWarnIronwatchGarrison: () => void
  onSeekCrownAudience: () => void
  onExposeFalseHistory: () => void
  onPledgeServiceToCrown: () => void
  onAcceptCommandOfRealm: () => void
  onCallTheRealmsToMuster: () => void
  onMeetAcademyInstructors: () => void
  onSeekElvenCourtAudience: () => void
  onOfferFealtyToElvenCrown: () => void
  onSearchTempleRecords: () => void
  onFaceAntiPlayer: () => void
  onRetakeIronwatch: () => void
  onOfferSeverance: () => void
  onFactionAction: (actionId: string) => void
  onBuyArmor: () => void
  onBack: () => void
}

const ATTACK_DAY_KNOWLEDGE = "orcs_attack_dawnmere_day2"
const ORC_SIGN_RUMOURS_KNOWLEDGE = "heard_orc_sign_rumours"
const ORC_WARBAND_SIGHTING_KNOWLEDGE = "heard_orc_warband_sighting"
const GUARD_WARNED_FLAG = "dawnmere_soldiers_convinced"
const EVACUATED_FLAG = "dawnmere_evacuated"
const BURNED_FLAG = "dawnmere_burned"
const RUINS_SECRET_KNOWLEDGE = "dawnmere_ruins_secret"

const GOLDENFIELDS_ATTACK_DAY_KNOWLEDGE = "orcs_raid_goldenfields_day12"
const GOLDENFIELDS_RUMOURS_KNOWLEDGE = "heard_goldenfields_rumours"
const GOLDENFIELDS_RIVERGATE_WARNING_KNOWLEDGE = "heard_goldenfields_warning_rivergate"
const GOLDENFIELDS_MILITIA_FLAG = "goldenfields_militia_rallied"
const GOLDENFIELDS_EVACUATED_FLAG = "goldenfields_evacuated"
const GOLDENFIELDS_DESTROYED_FLAG = "goldenfields_destroyed"
const GOLDENFIELDS_PREVENTED_FLAG = "goldenfields_raid_prevented"

const IRONWATCH_ATTACK_DAY_KNOWLEDGE = "orcs_besiege_ironwatch_day15"
const IRONWATCH_SCOUT_RUMOURS_KNOWLEDGE = "heard_orc_muster_rumours"
const BLACKTOOTH_SCOUTED_KNOWLEDGE = "scouted_blacktooth_wastes"
const IRONWATCH_PREVENTED_FLAG = "ironwatch_raid_prevented"
const IRONWATCH_FALLEN_FLAG = "ironwatch_fortress_controlled_by_enemy"

const REALMS_MUSTERED_FLAG = "realms_mustered"

const CHAMBERLAIN_DEAD_FLAG = "crownreach_chamberlain_dead"
const ALLIANCE_SABOTAGED_FLAG = "anti_player_sabotaged_an_alliance"

/**
 * What a place looks like after something happened to it that nobody stopped.
 * A deterministic event has to leave a visible, lasting trace for the rest of
 * the loop (see GAME_CREATION_INSTRUCTIONS.md) — the player should be able to
 * walk back in and read the consequence directly off the location rather than
 * having to infer it from a date they missed.
 *
 * `silencesActions` marks the places where there is genuinely no one left to
 * talk to; a scar without it still changes what the player sees while leaving
 * the location functional.
 */
interface LocationScar {
  locationId: string
  flag: string
  description: string
  silencesActions: boolean
}

const LOCATION_SCARS: LocationScar[] = [
  {
    locationId: "dawnmere",
    flag: BURNED_FLAG,
    description:
      "What's left of Dawnmere: collapsed timber, cold ash, the smell of smoke. Whoever survived is long gone. No one is left here to speak to.",
    silencesActions: true,
  },
  {
    locationId: "goldenfields",
    flag: GOLDENFIELDS_DESTROYED_FLAG,
    description:
      "The fields are black and smoking, fences torn down, the granary burned to its foundation. There is no one left to bring in what was left of the harvest.",
    silencesActions: true,
  },
  {
    locationId: "ironwatch_fortress",
    flag: IRONWATCH_FALLEN_FLAG,
    description:
      "Orc banners fly over Ironwatch's walls now. The gate hangs open, the garrison gone or worse. This is no longer a safe place to stand.",
    silencesActions: true,
  },
  {
    locationId: "ironwatch_fortress",
    flag: "ironwatch_dragon_burned_fortress",
    description:
      "Ironwatch is still standing, in the sense that its walls are. The yard is a long black scar, the granaries are ash, and the roofs of the inner keep have fallen in. Nobody here wants to talk about how long it circled before it started.",
    silencesActions: true,
  },
  {
    locationId: "rivergate",
    flag: "rivergate_docks_lost",
    description:
      "The Rivergate docks are shuttered and empty in the middle of the day. Nobody will say plainly what happened, only that people went out onto the wharf at night and did not come back, and that no one ever saw who did it.",
    silencesActions: false,
  },
  {
    locationId: "ruined_temple_first_dawn",
    flag: "temple_anchor_stands",
    description:
      "The temple is not empty any more. Something under the floor is holding the dead upright, and they are patient about it. Whatever knowledge was left here is on the far side of them now.",
    silencesActions: true,
  },
  {
    locationId: "kings_road_crossroads",
    flag: "kings_road_closed",
    description:
      "Giants hold the crossroads. The King's Road is closed in every direction that matters, the milestones smashed flat, and traffic that used to pass through here now simply doesn't.",
    silencesActions: true,
  },
  {
    locationId: "blacktooth_wastes",
    flag: "blacktooth_rift_widened",
    description:
      "The tear in the wastes is wider than it was, and steadier, as though it has settled into itself. The orc camps have grown up around it like a town around a well.",
    silencesActions: false,
  },
]

/**
 * The road from where the player is standing to the place they are looking at:
 * how long it takes, what it passes through, and a button for the first leg of
 * it. Travel is deliberately taken one leg at a time rather than in a single
 * jump, so that anything scheduled to happen along the way still happens.
 *
 * `continuation` marks the panel for a journey already under way — the player
 * is standing at a waystation between where they set out and where they meant
 * to go. In that mode the destination is carried onward through `onTravel` so
 * the next waystation can offer the same, and a "give up" affordance lets the
 * player abandon the trip and choose a new road; picking any other route does
 * the same implicitly.
 */
function JourneyPanel({
  permanent,
  loop,
  destinationId,
  onTravel,
  continuation = false,
  onCancelJourney,
}: {
  permanent: PermanentState
  loop: LoopState
  destinationId: string
  onTravel: (routeId: string, finalDestinationId?: string) => void
  continuation?: boolean
  onCancelJourney?: () => void
}) {
  const journey = getTravelPathTo(permanent, loop, destinationId)
  const nameOf = (id: string) => getLocationById(id)?.name ?? id

  if (!journey || journey.routeIds.length === 0) {
    // A closed border is a specific, actionable reason; say which trust would
    // open it rather than reporting a generic dead end.
    const blocked = getTravelBlockReason(loop, destinationId)
    return (
      <div className={`journey-panel${continuation ? " journey-panel-continue" : ""}`}>
        <p className="training-locked">
          {continuation
            ? `The road on to ${nameOf(destinationId)} is closed to you from here${
                blocked ? ` — ${blocked}` : "."
              }`
            : blocked ??
              `No open road leads here from ${nameOf(
                loop.currentLocationId,
              )} — either a border along the way is closed to you, or you have not yet found the road.`}
        </p>
        {continuation && onCancelJourney && (
          <button type="button" className="journey-cancel" onClick={onCancelJourney}>
            Abandon the journey
          </button>
        )}
      </div>
    )
  }

  const firstLeg = getRouteById(journey.routeIds[0])
  const firstStopId =
    firstLeg?.fromLocationId === loop.currentLocationId
      ? firstLeg?.toLocationId
      : firstLeg?.fromLocationId
  const isSingleLeg = journey.routeIds.length === 1
  // The button spends the *ridden* cost, not the raw road length, so it must
  // quote that — otherwise a trained horseman is promised a longer trip than
  // the calendar will actually take.
  const firstLegDays = firstLeg ? effectiveTravelDays(firstLeg.travelDays, permanent) : 0

  return (
    <div className={`journey-panel${continuation ? " journey-panel-continue" : ""}`}>
      {continuation && (
        <p className="journey-heading">
          You are on the road to <strong>{nameOf(destinationId)}</strong>.
        </p>
      )}
      <p>
        <strong>
          {journey.days} day{journey.days === 1 ? "" : "s"}
        </strong>{" "}
        of road {continuation ? "still ahead" : `from ${nameOf(loop.currentLocationId)}`} —
        you would arrive {formatDay(loop.currentDay + journey.days)}.
      </p>
      {!isSingleLeg && (
        <p className="journey-via">
          By way of {journey.via.map(nameOf).join(" → ")}.
        </p>
      )}
      <button type="button" onClick={() => onTravel(journey.routeIds[0], destinationId)}>
        {continuation
          ? isSingleLeg
            ? `Continue to ${nameOf(destinationId)}`
            : `Continue — next leg to ${nameOf(firstStopId ?? "")}`
          : isSingleLeg
            ? `Set Out for ${nameOf(destinationId)}`
            : `Set Out — first leg to ${nameOf(firstStopId ?? "")}`}{" "}
        ({firstLegDays} day{firstLegDays === 1 ? "" : "s"})
      </button>
      {continuation && onCancelJourney && (
        <button type="button" className="journey-cancel" onClick={onCancelJourney}>
          Abandon the journey — choose another road instead
        </button>
      )}
    </div>
  )
}

/** Who is standing with you here, and who is waiting to be picked up. */
function ForcesHere({ loop }: { loop: LoopState }) {
  const describe = (stacks: { unitId: string; count: number }[]) =>
    stacks
      .map((stack) => `${getUnitById(stack.unitId)?.name ?? stack.unitId} ×${stack.count}`)
      .join(", ")

  const waiting = getStacksAtLocation(loop, loop.currentLocationId)
  if (loop.currentArmy.length === 0 && waiting.length === 0) return null

  return (
    <dl className="status-line forces-here">
      {loop.currentArmy.length > 0 && (
        <>
          <dt>Marching with you</dt>
          <dd>{describe(loop.currentArmy)}</dd>
        </>
      )}
      {waiting.length > 0 && (
        <>
          <dt>Waiting here</dt>
          <dd>{describe(waiting)}</dd>
        </>
      )}
    </dl>
  )
}

/**
 * The capital armoury — the only sink for a loop's looted coin. Money dies with
 * the loop, but a suit bought here is recorded in permanent state and worn in
 * every life after, so a good run of won battles can be banked into a lasting
 * edge (see armorEngine). Suits are bought cheapest-first, each out of a single
 * loop's earnings.
 */
function Armoury({
  permanent,
  loop,
  onBuyArmor,
}: {
  permanent: PermanentState
  loop: LoopState
  onBuyArmor: () => void
}) {
  const current = getCurrentArmorTier(permanent)
  const next = getNextArmorTier(permanent)
  const canAfford = next !== null && loop.money >= next.cost
  return (
    <div className="armoury">
      <h3>The Royal Armoury</h3>
      <p className="marginalia">
        {current
          ? `You wear ${current.name}.`
          : "You wear nothing here worth the name of armour."}{" "}
        Purse: <strong>{loop.money}</strong> coin.
      </p>
      {next ? (
        <>
          <button type="button" disabled={!canAfford} onClick={onBuyArmor}>
            Buy {next.name} ({next.cost} coin)
          </button>
          <p className="marginalia">
            {next.blurb} Turns aside {Math.round(next.incomingReduction * 100)}% of
            incoming battle damage — and, once bought, it is yours across every loop to
            come.
          </p>
          {!canAfford && (
            <p className="training-locked">
              You cannot afford it yet — coin comes from won battles, and it does not
              carry past death, so it must be earned in a single life.
            </p>
          )}
        </>
      ) : (
        <p>There is nothing on these racks finer than what you already wear.</p>
      )}
    </div>
  )
}

export function LocationScreen({
  permanent,
  loop,
  locationId,
  travelDestinationId,
  onTravel,
  onCancelJourney,
  onTrainAbility,
  onOpenDialogue,
  onRest,
  onWarnGuard,
  onEvacuate,
  onInvestigateRuins,
  onRallyGoldenfieldsMilitia,
  onEvacuateGoldenfields,
  onSendWordToCrownreach,
  onWarnIronwatchGarrison,
  onSeekCrownAudience,
  onExposeFalseHistory,
  onPledgeServiceToCrown,
  onAcceptCommandOfRealm,
  onCallTheRealmsToMuster,
  onMeetAcademyInstructors,
  onSeekElvenCourtAudience,
  onOfferFealtyToElvenCrown,
  onSearchTempleRecords,
  onFaceAntiPlayer,
  onRetakeIronwatch,
  onOfferSeverance,
  onFactionAction,
  onBuyArmor,
  onBack,
}: LocationScreenProps) {
  const location = getLocationById(locationId)
  // Only show connections whose far end is already known — otherwise viewing
  // a discovered-but-unvisited location's card would leak the name of
  // somewhere the player hasn't found yet.
  const routes = getRoutesForLocation(locationId).filter((route) => {
    const destinationId =
      route.fromLocationId === locationId ? route.toLocationId : route.fromLocationId
    return permanent.discoveredLocations.includes(destinationId)
  })
  const isHere = locationId === loop.currentLocationId

  if (!location) {
    return (
      <div className="screen location-screen">
        <p>Unknown location.</p>
        <button type="button" onClick={onBack}>
          Back to Map
        </button>
      </div>
    )
  }

  // The first matching scar wins, so the table's order decides which of two
  // co-occurring disasters a place is described by.
  const scar = LOCATION_SCARS.find(
    (candidate) =>
      candidate.locationId === locationId && loop.worldFlags.includes(candidate.flag),
  )
  const isBurned = locationId === "dawnmere" && loop.worldFlags.includes(BURNED_FLAG)
  const isRuined = scar?.silencesActions ?? false

  // Only shows entries whose teacher is here (or which need no fixed
  // location) — so a locked entry always explains a choice relevant to this
  // screen, rather than listing every ability's status everywhere.
  const trainingEntries: UpcomingAbilityLevel[] = isRuined
    ? []
    : abilities.flatMap((ability) => {
        const upcoming = getUpcomingLevel(permanent, ability.id, locationId, loop)
        return upcoming && upcoming.atRequiredLocation ? [upcoming] : []
      })

  const diplomacyLevel = getAbilityLevel(permanent, "diplomacy")
  const historyLevel = getAbilityLevel(permanent, "history")
  const hasDiplomacyAdvanced = isAtLeastLevel(diplomacyLevel, "advanced")
  // Both Crownreach history actions grant a permanent, one-time discovery.
  // Once it is held, re-running them yields nothing, so the button should
  // retire rather than sit there inviting a wasted click.
  const hasConsultedHistorian = permanent.knowledge.includes("crownreach_history_context")
  const hasExposedFalseHistory = permanent.knowledge.includes("exposed_false_crownreach_history")

  const knowsAttackDay = permanent.knowledge.includes(ATTACK_DAY_KNOWLEDGE)
  const hasEvidence =
    permanent.knowledge.includes(ORC_SIGN_RUMOURS_KNOWLEDGE) ||
    permanent.knowledge.includes(ORC_WARBAND_SIGHTING_KNOWLEDGE)
  const hasWarnedGuard = loop.worldFlags.includes(GUARD_WARNED_FLAG)
  const hasEvacuated = loop.worldFlags.includes(EVACUATED_FLAG)
  const hasFoundRuinsSecret = permanent.knowledge.includes(RUINS_SECRET_KNOWLEDGE)

  const knowsGoldenfieldsAttackDay = permanent.knowledge.includes(
    GOLDENFIELDS_ATTACK_DAY_KNOWLEDGE,
  )
  const hasGoldenfieldsRumours = permanent.knowledge.includes(GOLDENFIELDS_RUMOURS_KNOWLEDGE)
  const hasRivergateWarning = permanent.knowledge.includes(
    GOLDENFIELDS_RIVERGATE_WARNING_KNOWLEDGE,
  )
  const hasGoldenfieldsEvidence = hasGoldenfieldsRumours || hasRivergateWarning
  const hasBothGoldenfieldsEvidence = hasGoldenfieldsRumours && hasRivergateWarning
  const hasRalliedMilitia = loop.worldFlags.includes(GOLDENFIELDS_MILITIA_FLAG)
  const hasEvacuatedGoldenfields = loop.worldFlags.includes(GOLDENFIELDS_EVACUATED_FLAG)
  const hasSentWordToCrownreach = loop.worldFlags.includes(GOLDENFIELDS_PREVENTED_FLAG)

  const knowsIronwatchAttackDay = permanent.knowledge.includes(IRONWATCH_ATTACK_DAY_KNOWLEDGE)
  const hasIronwatchEvidence =
    permanent.knowledge.includes(IRONWATCH_SCOUT_RUMOURS_KNOWLEDGE) ||
    permanent.knowledge.includes(BLACKTOOTH_SCOUTED_KNOWLEDGE)
  const hasWarnedIronwatch = loop.worldFlags.includes(IRONWATCH_PREVENTED_FLAG)
  // Ironwatch can fall to the orcs while the player is away. Unlike the villages
  // that share the "silences actions" scar, it is a fortress and can be stormed
  // back — but only by an army that marched here, not bare-handed.
  const ironwatchOrcHeld =
    locationId === "ironwatch_fortress" && loop.worldFlags.includes(IRONWATCH_FALLEN_FLAG)
  const hasMarchingArmy = loop.currentArmy.length > 0
  const hasMusteredRealms = loop.worldFlags.includes(REALMS_MUSTERED_FLAG)
  const chamberlainDead = loop.worldFlags.includes(CHAMBERLAIN_DEAD_FLAG)
  const alliancesSabotaged = loop.worldFlags.includes(ALLIANCE_SABOTAGED_FLAG)

  const ruinedDescription = scar?.description ?? null

  // Phase 7's five newer allied realms and seven hostile campaigns are all
  // data-driven (see questEngine) rather than hand-wired like the Human and
  // High Elf chains above — their buttons come from here.
  const factionActionEntries = isHere && !isRuined
    ? getFactionActionsAt(permanent, loop, locationId)
    : []

  const locationSpriteStyle = getLocationSpriteStyle(locationId)

  // Who holds this place, flown as its heraldic crest beside the name — the
  // same allegiance cue the map marker carries on hover, brought onto the
  // location's own page.
  const owner = location.factionId ? getFactionById(location.factionId) : null
  const ownerCrest = owner ? getFactionCrestStyle(owner.id) : null

  return (
    <div className="screen location-screen">
      <h1>
        {ownerCrest && (
          <span
            className="location-title-crest"
            style={ownerCrest}
            role="img"
            aria-label={owner ? `Held by ${owner.name}` : undefined}
          />
        )}
        {location.name}
      </h1>
      {/* A portrait of the place, cut from the location sheet. Absent only if
          its sheet has not been added yet, in which case nothing is drawn. */}
      {locationSpriteStyle && (
        <div
          className="location-image"
          style={locationSpriteStyle}
          role="img"
          aria-label={location.name}
        />
      )}
      {/* The place's flavour is the page's opening line, so it takes an
          illuminated initial the way a chronicle's first paragraph would. */}
      <p className="screen-lede">{ruinedDescription ?? location.description}</p>

      {/* Looking at somewhere you are not standing used to show nothing but a
          list of greyed-out roads, leaving the player to trace a path across
          the map by eye. The route graph already knows the shortest way; this
          says what it is, what it costs, and which road leaves town first. */}
      {!isHere && <JourneyPanel permanent={permanent} loop={loop} destinationId={locationId} onTravel={onTravel} />}

      {/* Standing at a waystation part way through a longer trip. Rather than
          make the player reopen the map and re-pick the same destination at
          every stop, the road they were already on offers to carry them the
          rest of the way — one leg at a time still, so events en route keep
          firing. Choosing any other road (or the give-up button) drops it. */}
      {isHere &&
        travelDestinationId !== null &&
        travelDestinationId !== loop.currentLocationId && (
          <JourneyPanel
            permanent={permanent}
            loop={loop}
            destinationId={travelDestinationId}
            onTravel={onTravel}
            continuation
            onCancelJourney={onCancelJourney}
          />
        )}

      {/* Soldiers parked at a location are invisible from the Location screen,
          and "Call the Realms to Muster" scatters six contingents across the
          map for the player to collect. Without this the player has to go
          through the Army screen to find out whether a detour was worth it. */}
      {isHere && <ForcesHere loop={loop} />}

      <h2>{isHere ? "Routes" : "Roads From Here"}</h2>
      <ul className="route-list">
        {routes.map((route) => {
          const destinationId =
            route.fromLocationId === locationId
              ? route.toLocationId
              : route.fromLocationId
          const destination = getLocationById(destinationId)
          // A closed border is shown rather than hidden, so the player learns
          // what trust would open it instead of wondering why the road ends.
          const trustBlockReason = getTravelBlockReason(loop, destinationId)
          // Spending days is the game's only real currency, so every button
          // that spends them says what day it lands on rather than making the
          // player add the cost to today's date — the *ridden* cost, so a
          // trained horseman's shorter road shows here and in the arrival date.
          const legDays = effectiveTravelDays(route.travelDays, permanent)
          const cost = `${legDays} day${legDays === 1 ? "" : "s"}`
          return (
            <li key={route.id}>
              {isHere ? (
                <button
                  type="button"
                  disabled={!isRouteOpen(route, permanent) || trustBlockReason !== null}
                  onClick={() => onTravel(route.id)}
                >
                  Travel to {destination?.name ?? destinationId} ({cost} — arrive{" "}
                  {formatDay(loop.currentDay + legDays)})
                </button>
              ) : (
                // Roads out of a place you are not standing in are a fact about
                // the map, not an action. They were previously rendered as
                // permanently dead buttons, which read as a bug.
                <p className="route-note">
                  {destination?.name ?? destinationId} — {cost} onward
                </p>
              )}
              {trustBlockReason && (
                <p className="training-locked">{trustBlockReason}</p>
              )}
            </li>
          )
        })}
      </ul>

      <h2>Actions</h2>
      <div className="action-list">
        {/* Forged in the player's own hand and already in front of the courts.
            Refutable — but not before this loop runs out, which is the point. */}
        {alliancesSabotaged && isHere && (
          <p className="training-locked marginalia">
            Letters in your handwriting have been laid before the courts,
            promising them to one enemy and selling them to another. No realm
            will advance its trust in you any further this year, whatever else
            you accomplish.
          </p>
        )}
        {isBurned && !hasFoundRuinsSecret && isHere && (
          <button type="button" onClick={onInvestigateRuins}>
            Investigate the Ruins (1 day) — a permanent discovery, you will not
            need to do this again
          </button>
        )}
        {isBurned && hasFoundRuinsSecret && (
          <p>You have already learned everything these ruins have to tell you.</p>
        )}
        {ironwatchOrcHeld && isHere && (
          <>
            {hasMarchingArmy ? (
              <button type="button" onClick={onRetakeIronwatch}>
                Assault the Fortress — retake Ironwatch from the orcs
              </button>
            ) : (
              <p className="training-locked">
                Orc banners fly over Ironwatch, and the gate is theirs. The
                fortress can be stormed back — but not bare-handed. Come to its
                gate with an army marching at your back and the yard can be
                yours again.
              </p>
            )}
          </>
        )}
        {isRuined && !isBurned && !ironwatchOrcHeld && (
          <p>There is little to do here now.</p>
        )}
        {!isRuined && locationId === "dawnmere" && isHere && (
          <>
            <button type="button" onClick={() => onOpenDialogue("dawnmere_villagers")}>
              Speak to Villagers
            </button>
            <button type="button" onClick={() => onOpenDialogue("dawnmere_guard")}>
              Visit the Local Guard
            </button>
            <button type="button" onClick={() => onOpenDialogue("dawnmere_rumours")}>
              Investigate Rumours
            </button>
            {knowsAttackDay && !hasWarnedGuard && (
              <button type="button" onClick={onWarnGuard}>
                Warn the Guard: &ldquo;The orcs will attack tomorrow.&rdquo;
                {!hasEvidence && " (no proof to offer)"}
              </button>
            )}
            {knowsAttackDay && !hasEvacuated && (
              <button type="button" onClick={onEvacuate}>
                Evacuate the Village
                {!hasEvidence && " (no proof to offer)"}
              </button>
            )}
          </>
        )}
        {locationId === "kings_road_crossroads" && isHere && (
          <button type="button" onClick={() => onOpenDialogue("crossroads_traveler")}>
            Talk to the Travelers
          </button>
        )}
        {locationId === "crownreach" && isHere && (
          <>
            <button type="button" onClick={() => onOpenDialogue("crownreach_townsfolk")}>
              Speak to the Townsfolk
            </button>
            {/* The Chamberlain is the only man in the building who can put a
                stranger in front of the king. With him dead there is no door
                into the court for the rest of this loop. */}
            {chamberlainDead && (
              <p className="training-locked marginalia">
                The antechamber is closed and there is a guard on it. The Court
                Chamberlain is dead, and he was the only person here who arranged
                audiences. Nothing further can be asked of this court this year.
              </p>
            )}
            {!chamberlainDead &&
              isAtLeastLevel(diplomacyLevel, "beginner") &&
              !isTrustAtLeast(loop, "humans", "recognized") && (
                <button type="button" onClick={onSeekCrownAudience}>
                  Seek an Audience with the Crown
                </button>
              )}
            {!chamberlainDead &&
              isAtLeastLevel(diplomacyLevel, "advanced") &&
              isTrustAtLeast(loop, "humans", "recognized") &&
              !isTrustAtLeast(loop, "humans", "trusted") && (
                <button type="button" onClick={onPledgeServiceToCrown}>
                  Pledge Service to the Crown
                </button>
              )}
            {!chamberlainDead &&
              isAtLeastLevel(diplomacyLevel, "master") &&
              isTrustAtLeast(loop, "humans", "trusted") &&
              !isTrustAtLeast(loop, "humans", "subjected") && (
                <button type="button" onClick={onAcceptCommandOfRealm}>
                  Accept Command of the Realm&rsquo;s Armies
                </button>
              )}
            {isTrustAtLeast(loop, "humans", "trusted") &&
              hasDiplomacyAdvanced &&
              !hasMusteredRealms && (
                <button type="button" onClick={onCallTheRealmsToMuster}>
                  Call the Realms to Muster — dwarves, wood elves, spellcasters and
                  spirits, each at their own posting
                </button>
              )}
            {hasMusteredRealms && (
              <p>
                The summons have gone out. Dwarven companies are forming at Ironwatch,
                the Sevenfold Tower&rsquo;s orders at Rivergate, Wood Elf pathfinders at
                Goldenfields, and the Spirit Wardens at the ruined temple. They will not
                march to you — you will have to go and collect them.
              </p>
            )}
            {isAtLeastLevel(historyLevel, "beginner") && !hasConsultedHistorian && (
              <button type="button" onClick={() => onOpenDialogue("crownreach_historian_insight")}>
                Consult the Royal Historian
              </button>
            )}
            {isAtLeastLevel(historyLevel, "advanced") && !hasExposedFalseHistory && (
              <button type="button" onClick={onExposeFalseHistory}>
                Expose the Court Historian&rsquo;s Lie — earns the crown&rsquo;s
                recognition
              </button>
            )}
            <Armoury permanent={permanent} loop={loop} onBuyArmor={onBuyArmor} />
          </>
        )}
        {locationId === "ruined_temple_first_dawn" && isHere && (
          <>
            <button type="button" onClick={() => onOpenDialogue("temple_search_ruins")}>
              Search the Ruins
            </button>
            {isAtLeastLevel(historyLevel, "beginner") &&
              permanent.knowledge.includes(RUINS_SECRET_KNOWLEDGE) && (
                <button type="button" onClick={() => onOpenDialogue("temple_old_records_insight")}>
                  Consult the Old Records
                </button>
              )}
            {/* The register that names him. Useless to anyone who has not met
                him — you have to know there is a name worth looking for. */}
            {canDiscoverIdentity(permanent) &&
              (isAtLeastLevel(historyLevel, "advanced") ? (
                <button type="button" onClick={onSearchTempleRecords}>
                  Search the Lower Vault for the Other Rememberer (1 day) — a
                  permanent discovery
                </button>
              ) : (
                <p className="training-locked">
                  Something in the lower vault would name the grey-haired man, but
                  the register predates the kingdom and its scheme is not one
                  anybody now alive was taught — locked: requires History
                  (advanced).
                </p>
              ))}
            {isAwaitingReckoning(permanent) && (
              <>
                <p>
                  He is sitting on the temple steps with his sword across his
                  knees, and he does not get up when you come into the courtyard.
                  He has stopped ambushing you. He is simply here, and he will be
                  here tomorrow.
                </p>
                <button type="button" onClick={onFaceAntiPlayer}>
                  Draw on Aldric Vane
                </button>
                {canSeverAntiPlayer(permanent) ? (
                  <button type="button" onClick={onOfferSeverance}>
                    Lay the Register Before Him — show him what he has been wrong
                    about for ninety-four years
                  </button>
                ) : (
                  <p className="training-locked marginalia">
                    There is another way to end this, and killing him has never
                    once worked — he wakes in Dawnmere with everything he knows.
                    Still missing:{" "}
                    {describeSeveranceRequirements(permanent).join("; ")}.
                  </p>
                )}
              </>
            )}
          </>
        )}
        {!isRuined && locationId === "goldenfields" && isHere && (
          <>
            <button type="button" onClick={() => onOpenDialogue("goldenfields_workers")}>
              Speak to the Farmhands
            </button>
            <button type="button" onClick={() => onOpenDialogue("goldenfields_rumours")}>
              Investigate Rumours
            </button>
            {knowsGoldenfieldsAttackDay && !hasRalliedMilitia && (
              <button type="button" onClick={onRallyGoldenfieldsMilitia}>
                Rally the Militia: &ldquo;The orcs will attack tomorrow.&rdquo;
                {!hasGoldenfieldsEvidence && " (no proof to offer)"}
              </button>
            )}
            {knowsGoldenfieldsAttackDay && !hasEvacuatedGoldenfields && (
              <button type="button" onClick={onEvacuateGoldenfields}>
                Evacuate Goldenfields
                {!hasGoldenfieldsEvidence && " (no proof to offer)"}
              </button>
            )}
            {knowsGoldenfieldsAttackDay &&
              !hasSentWordToCrownreach &&
              (hasBothGoldenfieldsEvidence ||
                (hasGoldenfieldsEvidence && hasDiplomacyAdvanced)) && (
                <button type="button" onClick={onSendWordToCrownreach}>
                  Send Word to Crownreach for Reinforcements
                </button>
              )}
          </>
        )}
        {locationId === "rivergate" && isHere && (
          <button type="button" onClick={() => onOpenDialogue("rivergate_warning")}>
            Talk to the Merchants
          </button>
        )}
        {!isRuined && locationId === "ironwatch_fortress" && isHere && (
          <>
            <button type="button" onClick={() => onOpenDialogue("ironwatch_scouts_rumours")}>
              Question the Scouts
            </button>
            {knowsIronwatchAttackDay && !hasWarnedIronwatch && (
              <button type="button" onClick={onWarnIronwatchGarrison}>
                Warn the Garrison: &ldquo;The orcs are mustering for an attack.&rdquo;
                {!hasIronwatchEvidence && " (no proof to offer)"}
              </button>
            )}
          </>
        )}
        {locationId === "blacktooth_wastes" && isHere && (
          <button type="button" onClick={() => onOpenDialogue("blacktooth_scouting")}>
            Scout the Wastes
          </button>
        )}
        {locationId === "starbow_academy" && isHere && (
          <>
            {!isTrustAtLeast(loop, "high_elves", "recognized") ? (
              <button type="button" onClick={onMeetAcademyInstructors}>
                Meet the Academy Instructors
              </button>
            ) : (
              <p>The instructors already know you. There is little more to learn here.</p>
            )}
          </>
        )}
        {locationId === "moonspire" && isHere && (
          <>
            <button type="button" onClick={() => onOpenDialogue("moonspire_greeting")}>
              Take In the Elven Capital
            </button>
            {!isTrustAtLeast(loop, "high_elves", "recognized") && (
              <p className="training-locked marginalia">
                The Elven Court will not receive a stranger. Perhaps the instructors at Starbow
                Academy could vouch for you first.
              </p>
            )}
            {isTrustAtLeast(loop, "high_elves", "recognized") &&
              !isTrustAtLeast(loop, "high_elves", "trusted") &&
              isAtLeastLevel(diplomacyLevel, "beginner") && (
                <button type="button" onClick={onSeekElvenCourtAudience}>
                  Seek an Audience with the Elven Court
                </button>
              )}
            {isTrustAtLeast(loop, "high_elves", "trusted") &&
              !isTrustAtLeast(loop, "high_elves", "subjected") &&
              isAtLeastLevel(diplomacyLevel, "master") && (
                <button type="button" onClick={onOfferFealtyToElvenCrown}>
                  Offer Fealty to the Elven Crown
                </button>
              )}
          </>
        )}
        {locationId === "cloudwood_aerie" && isHere && (
          <button type="button" onClick={() => onOpenDialogue("cloudwood_aerie_watch")}>
            Speak to the Watchers
          </button>
        )}
        {factionActionEntries.map(({ action, unlocked, lockedHint, costDays, activeShortcut }) =>
          unlocked ? (
            <div key={action.id} className="faction-action">
              <button type="button" onClick={() => onFactionAction(action.id)}>
                {action.label}
                {costDays > 0
                  ? ` (${costDays} day${costDays === 1 ? "" : "s"}, finishing ${formatDay(
                      loop.currentDay + costDays,
                    )})`
                  : ""}
              </button>
              {/* Phase 9 knowledge shortcut: say plainly that this is faster
                  because the player has done it before, and by how much. */}
              {activeShortcut && (
                <p className="shortcut-note marginalia">
                  {activeShortcut.note} You have done this before — {action.costDays} day
                  {action.costDays === 1 ? "" : "s"}&rsquo; work, cut to {costDays}.
                </p>
              )}
            </div>
          ) : (
            <p key={action.id} className="training-locked">
              {lockedHint}
            </p>
          ),
        )}
        {isHere && (
          <button type="button" onClick={onRest}>
            Rest (1 day — wake {formatDay(loop.currentDay + 1)})
          </button>
        )}
      </div>

      {trainingEntries.length > 0 && (
        <>
          <h2>Training</h2>
          <ul className="training-list">
            {trainingEntries.map(({
              ability,
              levelDefinition,
              trainable,
              missingRequirements,
              missingTrust,
              teacherLost,
            }) => {
              const spriteStyle = getAbilitySpriteStyle(ability.id)
              return (
              <li key={ability.id} className="training-entry">
                {spriteStyle && (
                  <div
                    className="ability-icon"
                    style={spriteStyle}
                    role="img"
                    aria-label={ability.name}
                  />
                )}
                <div className="training-entry-body">
                {teacherLost ? (
                  // No requirement will unlock this: the person who taught it
                  // is dead for the rest of the loop.
                  <p className="training-locked">
                    {ability.name} ({levelDefinition.level}) — there is nobody
                    left here to teach it. Whoever did is dead, and the loop will
                    not put them back before it turns over.
                  </p>
                ) : trainable ? (
                  <button
                    type="button"
                    disabled={!isHere}
                    onClick={() => onTrainAbility(ability.id, levelDefinition.level)}
                  >
                    Train {ability.name} ({levelDefinition.level}) —{" "}
                    {levelDefinition.trainingDays} day
                    {levelDefinition.trainingDays === 1 ? "" : "s"}, finishing{" "}
                    {formatDay(loop.currentDay + levelDefinition.trainingDays)}
                  </button>
                ) : (
                  <p className="training-locked">
                    {ability.name} ({levelDefinition.level}) — locked: requires{" "}
                    {[
                      ...missingRequirements.map(describeRequirement),
                      ...(missingTrust
                        ? [`the trust of the ${describeTrustRequirement(missingTrust)}`]
                        : []),
                    ].join(", ")}
                  </p>
                )}
                </div>
              </li>
              )
            })}
          </ul>
        </>
      )}

      <button type="button" onClick={onBack}>
        Back to Map
      </button>
    </div>
  )
}
